import * as React from 'react';
import { merge } from 'lodash-es';
import {
  StackLayout,
  StackItem,
  ParagraphText,
  type GDSHeadingLevel,
  type GDSSpaceToken,
  spacingTokenMap,
  visuallyHiddenStyle,
  type GDSSpace,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { SectionHeader } from '../masonry-section-header-renderer/section-header.component';
import { COMPLEX_WIDGET_CARDS_WIDTH } from '../../constants';
import { MasonryTextAction } from '../common/masonry-text-action/masonry-text-action.component';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

export interface MasonryHorizontalLayoutNodeProperties {
  body?: string;
  headerButtonLabel?: string;
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  isSemanticList?: boolean;
  spacing?: GDSSpaceToken;
  sectionHeaderPaddingTop?: GDSSpace;
  wrap?: boolean;
}

export type MasonryHorizontalLayoutNodeActions = {
  onHeaderButtonClick?: MasonryDriverAction;
};

export type MasonryHorizontalLayoutNode = MasonryEngineNode<
  'horizontalLayout',
  MasonryHorizontalLayoutNodeProperties,
  MasonryHorizontalLayoutNodeActions,
  {
    items: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryHorizontalLayoutRendererProps =
  MasonryEngineNodeRendererProps<MasonryHorizontalLayoutNode>;

export const MasonryHorizontalLayoutRenderer = ({
  body,
  headerButtonLabel,
  heading,
  headingLevel,
  isSemanticList,
  items,
  onHeaderButtonClick,
  spacing = 'one',
  className,
  engineClassName,
  rootElementRef,
  locatorId,
  css,
  sectionHeaderPaddingTop,
  wrap,
}: MasonryHorizontalLayoutRendererProps) => {
  const hasTextAction: boolean = Boolean(
    onHeaderButtonClick && headerButtonLabel,
  );

  if (!items) return null;
  const itemSpacing = spacingTokenMap[spacing];

  return (
    <StackLayout
      spacing="$one"
      horizontalAlignment="stretch"
      orientation="vertical"
      className={formatClassNames(engineClassName, className)}
      ref={rootElementRef}
      data-testid={locatorId}
      css={css}
    >
      {heading && (
        <SectionHeader
          headingLevel={headingLevel}
          heading={heading}
          paddingTop={sectionHeaderPaddingTop}
          textAction={
            hasTextAction ? (
              <MasonryTextAction
                onClick={onHeaderButtonClick}
                text={headerButtonLabel}
              />
            ) : undefined
          }
        />
      )}
      {body && <ParagraphText>{body}</ParagraphText>}
      <StackLayout
        as={isSemanticList ? 'ul' : 'div'}
        horizontalAlignment="stretch"
        orientation="horizontal"
        spacing={itemSpacing ?? '$one'}
        verticalAlignment="stretch"
        css={{ flexWrap: wrap ? 'wrap' : undefined }}
      >
        {items &&
          React.Children.map(
            items,
            (item) =>
              item && (
                <StackItem
                  as={isSemanticList ? 'li' : 'div'}
                  css={{
                    minWidth: COMPLEX_WIDGET_CARDS_WIDTH,
                    '&:empty': {
                      display: 'none',
                    },
                    '&:has(> .visually-hidden)': {
                      ...visuallyHiddenStyle,
                    },
                  }}
                  verticalAlignment="fillContainer"
                >
                  {React.cloneElement(item, {
                    node: merge({}, (item as React.ReactElement).props.node, {
                      properties: { isChildItem: true },
                    }),
                  })}
                </StackItem>
              ),
          )}
      </StackLayout>
    </StackLayout>
  );
};
